/**
 * Application state: the selected run, the timestep, the visible layers and the
 * scenario being edited.
 *
 * Server data is NOT kept here - TanStack Query owns that. This store holds
 * only what the user has chosen, so a refetch never clobbers a selection and a
 * selection never goes stale against the cache.
 */

import { create } from 'zustand'

/**
 * Raster layers, matching the `overlay/{layer}` path values exactly so the
 * panel cannot drift from the API.
 *
 * `depth` and `velocity` vary with time and take `?t=`; the rest are static.
 * Roads, buildings and the satellite extent are Phase 5 and Phase 6 and are
 * deliberately absent - a toggle with no endpoint behind it is a stub.
 */
export const TIME_VARYING_LAYERS = ['depth', 'velocity'] as const
export const STATIC_LAYERS = [
  'max_depth',
  'max_velocity',
  'max_dv',
  'arrival',
  'duration',
  'hazard',
] as const

export type RasterLayer =
  | (typeof TIME_VARYING_LAYERS)[number]
  | (typeof STATIC_LAYERS)[number]

export const LAYER_LABELS: Record<RasterLayer, string> = {
  depth: 'Water depth',
  velocity: 'Flow velocity',
  max_depth: 'Max depth',
  max_velocity: 'Max velocity',
  max_dv: 'Depth-velocity',
  arrival: 'Arrival time',
  duration: 'Duration',
  hazard: 'Hazard zones',
}

export function isTimeVarying(layer: RasterLayer): boolean {
  return (TIME_VARYING_LAYERS as readonly string[]).includes(layer)
}

export interface ClickedPoint {
  lat: number
  lon: number
}

interface AppState {
  /** The run whose results are on screen. Null only before the first load. */
  selectedRunId: string | null
  setSelectedRunId: (runId: string | null) => void

  /**
   * Exactly one raster layer is drawn at a time. Stacking two semi-transparent
   * ramps produces a colour that is in neither legend, which is worse than
   * useless on a hazard map.
   */
  activeLayer: RasterLayer
  setActiveLayer: (layer: RasterLayer) => void

  /** Independent vector overlays, drawn above whichever raster is active. */
  showExtent: boolean
  showSettlements: boolean
  showReach: boolean
  toggleExtent: () => void
  toggleSettlements: () => void
  toggleReach: () => void

  /** Index into `Timesteps.times_s`, not a time in seconds. */
  frameIndex: number
  setFrameIndex: (index: number) => void
  playing: boolean
  setPlaying: (playing: boolean) => void

  /** Where the user last clicked, driving the FeatureInspector. */
  clickedPoint: ClickedPoint | null
  setClickedPoint: (point: ClickedPoint | null) => void

  /** The scenario the setup form last saved, offered to /simulation. */
  activeScenarioId: number | null
  setActiveScenarioId: (id: number | null) => void

  /** Part 11: the site every page is scoped to. Kept per browser. */
  activeSiteId: string
  setActiveSiteId: (id: string) => void
}

const SITE_KEY = 'dss.activeSiteId'

function storedSite(): string {
  try {
    return window.localStorage.getItem(SITE_KEY) || 'UJJANI'
  } catch {
    return 'UJJANI'
  }
}

export const useAppStore = create<AppState>((set) => ({
  selectedRunId: null,
  setSelectedRunId: (runId) =>
    // A new run means a new time axis and a new click target; carrying either
    // over would show frame 40 of a run that has 12 frames.
    set({ selectedRunId: runId, frameIndex: 0, playing: false, clickedPoint: null }),

  activeLayer: 'depth',
  setActiveLayer: (layer) => set({ activeLayer: layer }),

  showExtent: false,
  showSettlements: true,
  showReach: true,
  toggleExtent: () => set((state) => ({ showExtent: !state.showExtent })),
  toggleSettlements: () =>
    set((state) => ({ showSettlements: !state.showSettlements })),
  toggleReach: () => set((state) => ({ showReach: !state.showReach })),

  frameIndex: 0,
  setFrameIndex: (index) => set({ frameIndex: index }),
  playing: false,
  setPlaying: (playing) => set({ playing }),

  clickedPoint: null,
  setClickedPoint: (point) => set({ clickedPoint: point }),

  activeScenarioId: null,
  setActiveScenarioId: (id) => set({ activeScenarioId: id }),

  activeSiteId: storedSite(),
  setActiveSiteId: (id) => {
    try {
      window.localStorage.setItem(SITE_KEY, id)
    } catch {
      // Storage blocked (private mode): the choice lasts for this tab only.
    }
    set({ activeSiteId: id })
  },
}))
